/**
 * ApiBaseUrlUsageCard — Fix-Agent Developer-Redesign (v2-final/developer.html)
 *
 * Ersetzt den fehlenden Header-Kasten der Design-Vorlage: v2-Basis-URL
 * (niemals die alte v1-Domain, ACCEPTANCE "Developer: v2 base URL only")
 * mit Copy-Button, plus kompakter Kontingent-Balken direkt daneben.
 *
 * Wird nur gerendert, wenn der Account bereits Public-API-Zugang hat (der
 * Aufrufer — DeveloperPortalPage — prueft das vorher einmalig, siehe
 * "uniform premium gating").
 */
import { useState, useCallback } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ApiUsage } from './useApiUsage'

// heliumtracker-web#9: die im Developer-Portal angezeigte Basis-URL ist
// IMMER die v2-Customer-API (dieselbe Konfiguration wie api-client.ts),
// nie die alte v1-Public-API-Domain.
export const API_V2_BASE_URL = `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''}/api/v2`

type Props = { usage: ApiUsage }

export function ApiBaseUrlUsageCard({ usage }: Props) {
  const { t, i18n } = useTranslation('developer')
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(API_V2_BASE_URL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard-API nicht verfügbar
    }
  }, [])

  const pct = Math.min(Math.max(usage.quota_used_pct, 0), 100)
  const barColor =
    usage.quota_used_pct >= 100 ? 'var(--danger)' : usage.quota_used_pct >= 80 ? 'var(--warning)' : 'var(--primary)'

  return (
    <section
      className="card card-pad"
      data-testid="api-base-url-card"
      aria-label={t('baseUrl.title')}
      style={{
        marginBottom: 18,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-subtle)' }}>
          {t('baseUrl.title')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, fontSize: 14, fontFamily: 'ui-monospace, Consolas, monospace', wordBreak: 'break-all' }}>
          <span data-testid="api-base-url-value">{API_V2_BASE_URL}</span>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={() => { void handleCopy() }}
            aria-label={t('baseUrl.copyAria')}
            data-testid="api-base-url-copy"
            style={{ width: 22, height: 22, flexShrink: 0 }}
          >
            {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <div style={{ minWidth: 220, flex: '0 1 260px' }} data-testid="api-base-url-meter">
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-subtle)' }}>
          <span>{t('baseUrl.usageLabel')}</span>
          <span style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>
            {t('baseUrl.usageCount', {
              used: usage.quota_used.toLocaleString(i18n.language),
              limit: usage.quota_limit.toLocaleString(i18n.language),
            })}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('apiQuota.progressAria', { pct: usage.quota_used_pct })}
          style={{
            height: 6,
            borderRadius: 999,
            background: 'color-mix(in srgb, var(--text) 10%, transparent)',
            overflow: 'hidden',
            marginTop: 5,
          }}
        >
          <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
        </div>
      </div>
    </section>
  )
}
